/**
 * activityAdapter — translate API activity items to the shape the preview
 * components expect.
 *
 * Background: backend `chat_messages` stores role as `user | assistant |
 * system`. The preview ActivityLog renders bubbles based on `customer | ai |
 * system`. Without remapping, user messages lose their styling and AI replies
 * land under the wrong selector — this file owns that mapping.
 */

import { stripControlJson, EMPTY_REPLY_FALLBACK } from './messageContent'

function cleanText(content, fallbackOnEmptyAssistant = false) {
  const cleaned = stripControlJson(content || '')
  if (!cleaned && fallbackOnEmptyAssistant) return EMPTY_REPLY_FALLBACK
  return cleaned
}

// Backend persists completion issues as raw strings — some include ORM
// column names ("transitDays, validUntil") and others are pure debug
// signals that are useful in logs but noise on a buyer's quote card.
const QUOTE_FIELD_LABELS = {
  totalCost: 'Total cost',
  transitDays: 'Transit time',
  validUntil: 'Quote validity',
  oceanFreight: 'Ocean freight',
  fuelSurcharge: 'Fuel surcharge',
  originCharges: 'Origin charges',
  destinationCharges: 'Destination charges',
  truckType: 'Truck type',
  perMileRate: 'Per-mile rate',
  totalMiles: 'Total miles',
  pickupDate: 'Pickup date',
  deliveryDate: 'Delivery date'
}
// Strings that exist for internal observability but don't help a buyer
// decide. Hidden from the buyer-facing list.
const DEBUG_ONLY_ISSUE_PATTERNS = [
  /^low extraction confidence$/i,
  /^multiple plausible totals detected$/i,
  /^ambiguous /i
]

function humanizeFieldList(raw) {
  return raw
    .split(',')
    .map(s => s.trim())
    .filter(Boolean)
    .map(name => QUOTE_FIELD_LABELS[name] || name)
    .join(', ')
}

export function adaptCompletionIssues(issues) {
  if (!Array.isArray(issues)) return []
  const out = []
  for (const raw of issues) {
    const issue = typeof raw === 'string' ? raw : String(raw ?? '')
    if (!issue) continue
    if (DEBUG_ONLY_ISSUE_PATTERNS.some(re => re.test(issue))) continue
    const missingMatch = issue.match(/^Missing required fields:\s*(.+)$/i)
    if (missingMatch) {
      out.push(`Missing required fields: ${humanizeFieldList(missingMatch[1])}`)
      continue
    }
    out.push(issue)
  }
  return out
}

export function adaptActivityItem(item) {
  // API returns { id, kind, role, content, payload, createdAt }
  // Preview expects { id, kind, role, text, author, payload, ... }
  const adapted = {
    id: item.id,
    kind: item.kind || 'message',
    payload: item.payload || null,
    dismissed: !!item.dismissedAt
  }

  if (item.kind === 'message' || !item.kind) {
    if (item.role === 'assistant') {
      adapted.role = 'ai'
      adapted.author = 'Marq'
      adapted.text = cleanText(item.content, true)
    } else if (item.role === 'system') {
      adapted.role = 'system'
      adapted.author = 'System'
      adapted.text = cleanText(item.content)
    } else {
      adapted.role = 'customer'
      adapted.author = 'You'
      // User-authored content shouldn't contain control JSON, but the helper
      // is a no-op for clean prose so applying it uniformly is safe.
      adapted.text = cleanText(item.content)
    }
  } else if (item.kind === 'system') {
    adapted.text = cleanText(item.content)
  } else if (item.kind === 'quote_card') {
    Object.assign(adapted, item.payload || {})
    adapted.kind = 'quote'
    adapted.text = item.content || null
    adapted.received = item.createdAt
      ? new Date(item.createdAt).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
      : null
    // Humanize the ORM-flavored issue list so buyers don't see raw column
    // names like "transitDays". Also strips debug-only signals.
    if (adapted.completionIssues != null) {
      adapted.completionIssues = adaptCompletionIssues(adapted.completionIssues)
    }
  } else if (item.kind === 'compare_card') {
    Object.assign(adapted, item.payload || {})
    adapted.kind = 'compare'
    adapted.text = item.content || null
  } else if (item.kind === 'booking_choice') {
    // Renders an inline card with two radios ("Marq sends booking intent" /
    // "I'll contact them on my end"). Submit hits POST /booking-intent and
    // the backend appends a follow-up booking_sent or booking_handed_off
    // activity that closes the chat input.
    adapted.role = 'system'
    adapted.author = 'Marq'
    adapted.text = item.content || ''
    adapted.quoteId = item.payload?.quoteId || null
    adapted.providerId = item.payload?.providerId || null
    adapted.providerName = item.payload?.providerName || null
    adapted.providerContactEmail = item.payload?.providerContactEmail || null
    adapted.ownerEmail = item.payload?.ownerEmail || null
    adapted.totalCost = item.payload?.totalCost ?? null
    adapted.currency = item.payload?.currency || 'USD'
  } else if (item.kind === 'booking_sent') {
    // Final dialog after the user picks "Marq sends". Renders as the
    // "Your part is done" card. Chat input is removed when this kind
    // (or booking_handed_off) is present in the activity.
    adapted.role = 'system'
    adapted.author = 'Marq'
    adapted.text = item.content || ''
    adapted.providerName = item.payload?.providerName || null
    adapted.to = item.payload?.to || null
    adapted.cc = item.payload?.cc || null
    adapted.subject = item.payload?.subject || null
    adapted.bodySnippet = item.payload?.bodySnippet || null
    adapted.trackingId = item.payload?.trackingId || null
    adapted.emailSent = item.payload?.emailSent === true
    adapted.emailError = item.payload?.emailError || null
  } else if (item.kind === 'booking_handed_off') {
    // Final dialog after the user picks "I'll contact them on my end".
    // No email preview — just acknowledges the hand-off and closes the chat.
    adapted.role = 'system'
    adapted.author = 'Marq'
    adapted.text = item.content || ''
    adapted.providerName = item.payload?.providerName || null
    adapted.providerContactEmail = item.payload?.providerContactEmail || null
    adapted.trackingId = item.payload?.trackingId || null
  } else if (item.kind === 'carrier_question') {
    // Surface the carrier's body snippet so the chat can show "what they
    // actually wrote", not just the system summary. Without this branch the
    // adapter falls into the generic `else` and the snippet stays buried in
    // the payload — the buyer reads "review the carrier message" but never
    // sees the message.
    adapted.role = 'system'
    adapted.author = 'System'
    adapted.text = item.content || ''
    adapted.providerName = item.payload?.providerName || null
    adapted.subject = item.payload?.subject || null
    adapted.bodySnippet = item.payload?.bodySnippet || null
    // MTEU-744/746: the carrier's outstanding asks, model-extracted, plus
    // the price the same reply committed (if any). The pending-items block
    // under the comparison table renders these.
    adapted.openQuestions = Array.isArray(item.payload?.openQuestions) ? item.payload.openQuestions : []
    adapted.quotedCost = item.payload?.quotedCost ?? null
    adapted.currency = item.payload?.currency || null
  } else if (item.kind === 'carrier_declined') {
    // Same shape as carrier_question — the decline reason + body snippet
    // belong in the bubble so the user can see *why* the carrier passed.
    adapted.role = 'system'
    adapted.author = 'System'
    adapted.text = item.content || ''
    adapted.providerName = item.payload?.providerName || null
    adapted.declineReason = item.payload?.declineReason || null
    adapted.bodySnippet = item.payload?.bodySnippet || null
  } else if (item.kind === 'carrier_needs_review') {
    // Carrier replied but the parser flagged the response for human review
    // (LOW extraction confidence, ambiguous totals, implausible values, etc.)
    // The auto-nudge was suppressed; the buyer needs to see WHAT the carrier
    // wrote and WHY the parser couldn't act on it.
    adapted.role = 'system'
    adapted.author = 'System'
    adapted.text = item.content || ''
    adapted.providerName = item.payload?.providerName || null
    adapted.subject = item.payload?.subject || null
    adapted.bodySnippet = item.payload?.bodySnippet || null
    adapted.issues = Array.isArray(item.payload?.issues) ? item.payload.issues : []
  } else if (item.kind === 'provider_selection') {
    adapted.candidates = item.payload?.candidates || []
    adapted.suggestedCount = item.payload?.suggestedCount || item.payload?.recommendedCount || 3
    adapted.tenantMedianCount = item.payload?.tenantMedianCount ?? null
    adapted.quoteType = item.payload?.quoteType || null
    // Reopened flag — surfaces the picker again after a dispatch when Marq
    // recalls it via "show me the providers" intent. The V2WorkspacePage
    // pendingSelection filter checks this to bypass hasQuoteWorkflowStarted.
    adapted.reopened = !!item.payload?.reopened
  } else {
    adapted.text = item.content || ''
  }

  return adapted
}
